// File: packages/types/src/public_agents/agentCalculatorTypes.ts

/**
 * =================================================================
 * DOPOMOGAI CALCULATOR: CANONICAL TYPES
 * =================================e=================================
 * This file is the single source of truth for the Pricing Calculator
 * data model. It unifies concepts from all previous versions into
 * one robust, scalable, and well-documented architecture, directly
 * reflecting the structure of `calculatorSpec.json` and its consumer hooks.
 */

// SECTION 1: CORE PRIMITIVES
// -----------------------------------------------------------------

/** A unique identifier, typically a string. */
export type ID = string;

/** A numeric range represented as a tuple: `[min, max]`. */
export type Range = [number, number];

/** The currency code, e.g., 'USD'. */
export type Currency = 'USD' | string;

/** The tier of an agent, used for gating features. */
export type TierId = 'trainee' | 'junior' | 'middle' | 'senior';

/** Controls the visibility of a module in the UI. */
export type Visibility = 'default' | 'advanced';


// SECTION 2: DOMAIN MODELS (from calculatorSpec.json)
// -----------------------------------------------------------------

/**
 * The baseline offering. Its price and impacts are the starting
 * point before any modules are added. Corresponds to the `base` object in the spec.
 */
export type CalcBase = {
  priceUSD: number;
  etaDays: Range;
  convLiftPp: Range;
  supportReductionPct: Range;
  includes: string[];
  workstreams?: string[]; // Used for parallel ETA calculation
};

/**
 * A single, selectable feature or capability in the calculator.
 * Corresponds to a `feature` object within a `group` in the spec.
 */
export type Module = {
  id: ID;
  label: string;
  desc: string;
  group: string;
  priceUSD: number;
  etaDays: Range;
  tierGate: TierId;
  recommended?: boolean;
  visibility?: Visibility;
  
  // For ETA calculation
  workstreams?: string[];

  // For dependency and compatibility checks
  platformIncompatibilities?: ID[];
  dependsOn?: ID[];
  
  // For ROI calculation
  convLiftPp: Range;
  supportReductionPct: Range;
};

/**
 * A logical grouping of modules for presentation in the UI.
 * Corresponds to a `group` object in the spec.
 */
export type ModuleGroup = {
  id: ID;
  label: string;
  features: Module[];
};

/**
 * A selectable target platform that may affect compatibility and ETA.
 * Corresponds to a `platform` object in the spec.
 */
export type Platform = {
  id: ID;
  label: string;
  notes?: string;
  etaDays: Range; // Additional ETA for platform-specific work
  priceAdj?: number; // Optional additive adjustment to base price
};

/**
* A curated set of modules that can be selected with one click.
* Can optionally include a price override for the contained modules.
* Corresponds to a `preset` object in the spec.
*/
export type Preset = {
  id: ID;
  label: string;
  desc?: string;
  moduleIds: ID[];
  priceOverrideUSD?: number;
  recommended?: boolean;
};

/**
 * Optional caps applied to cumulative impacts to keep them realistic.
 * Corresponds to the `caps` object in the spec.
 */
export type CalculatorCaps = {
  convLiftPpMax?: number;
  supportReductionPctMax?: number;
};

/** Default caps for calculator impacts if not provided in the spec. */
export const DEFAULT_CAPS: Required<CalculatorCaps> = {
  convLiftPpMax: 1.2,
  supportReductionPctMax: 60,
};


// SECTION 3: THE CALCULATOR SPECIFICATION
// -----------------------------------------------------------------

/**
 * Segment configuration for price and time multipliers.
 */
export type CalculatorSegment = {
  key: string;
  price_multiplier?: number;
  time_multiplier?: number;
};

/**
 * The complete, self-contained specification for a calculator instance.
 * This is the primary data structure loaded by the calculator from `calculatorSpec.json`.
 */
export type CalculatorSpec = {
  title: string;
  currency: Currency;
  socialImpactPct: number;
  base: CalcBase;
  groups: ModuleGroup[];
  platforms: Platform[];
  presets: Preset[];
  caps?: CalculatorCaps;
  cosmetics_map?: Record<ID, string[]>;
  segments?: CalculatorSegment[];
  // --------------
  roiModelConfig: {
    defaultInputs: Partial<ROIInputs>;
    showRevenueLift: boolean;
  };
  disclaimers: string[];
};


// SECTION 4: STATE & CONTEXT (for React Hooks)
// -----------------------------------------------------------------

/**
 * The minimal, serializable state for sharing via URL or localStorage.
 */
export type ShareableCalculatorState = {
  platformId?: ID;
  presetId?: ID;
  selectedModuleIds?: ID[];
  roi?: Partial<ROIInputs>;
};

/**
 * The full, real-time state managed by the `useCalculatorState` hook.
 */
export type CalculatorState = {
  platformId: ID;
  presetId?: ID;
  selectedModuleIds: Set<ID>; // A Set is used for efficient add/delete/has checks.
  roiInputs: ROIInputs;
  segmentKey?: 'smb' | 'mid' | 'enterprise';
};

/**
 * The explicit actions provided by `useCalculatorState` to manipulate state.
 */
export type CalculatorActions = {
  setPlatform: (platformId: ID) => void;
  setPreset: (presetId?: ID) => void;
  toggleModule: (moduleId: ID) => void;
  setRoiInputs: (patch: Partial<ROIInputs>) => void;
  setSegment: (segmentKey?: 'smb' | 'mid' | 'enterprise') => void;
  addWithDependencies: (moduleId: ID) => void;
  removeWithDependents: (moduleId: ID) => void;
  clear: () => void;
};

/**
 * Derived, calculated totals based on the current state, computed by `useTotals`.
 */
export type CalculatorTotals = {
  totalPriceUSD: number;
  etaDays: Range;
  etaWeeks: Range;
  convLiftPp: Range;
  supportReductionPct: Range;
  currentTier: TierId;
};

/**
 * The shape of the context value provided by `CalculatorProvider`.
 */
export type CalculatorContextValue = {
  spec: CalculatorSpec;
  state: CalculatorState;
  actions: CalculatorActions;
};


// SECTION 5: SUPPORTING MODELS (for ROI and Payloads)
// -----------------------------------------------------------------

/**
 * The set of user-provided inputs for the ROI calculation model.
 */
export type ROIInputs = {
  sessions?: number;
  cvrPct?: number;
  aov?: number;
  marginPct?: number;
  ticketsMonthly?: number;
  costPerTicket?: number;
};

/**
 * The set of calculated outputs from the ROI model.
 */
export type ROIOutputs = {
  addedOrdersRange?: Range;
  addedRevenueRange?: Range;
  grossProfitImpactRange?: Range;
  supportSavingsRange?: Range;
  totalMonthlyImpactRange?: Range;
};

/**
 * A snapshot payload representing the final state of the calculator.
 * This is what the component would emit when a user clicks "Get Quote" or "Finalize".
 */
export type QuotePayload = {
  platformId: ID;
  selectedModuleIds: ID[];
  presetId?: ID;
  totals: CalculatorTotals;
  currency: Currency;
  roi?: {
    inputs?: ROIInputs;
    outputs?: ROIOutputs;
  };
  shareUrl: string;
};